import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Eye, EyeOff } from "lucide-react";
import { isApiConfigured, postSignin } from "../apiClient";
import { buildAuthHrefWithNext, navigateAfterSignIn } from "../navigateAfterAuth";
import PageSeo from "../components/PageSeo";
import Nd1AuthShell from "../nd1/components/Nd1AuthShell";
import Nd1SigninMarketing from "../nd1/components/Nd1SigninMarketing";
import "../nd1/nd1.css";

function SigninPage({ onSignIn, currentUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const nextParam = searchParams.get("next");
  const signupHref = useMemo(
    () => buildAuthHrefWithNext("/signup", nextParam),
    [nextParam]
  );
  const showWelcome = Boolean(location.state?.welcome);
  const signupData = location.state?.signupData || null;
  const passwordResetHint = Boolean(location.state?.passwordReset);
  const resetEmailHint = typeof location.state?.emailHint === "string" ? location.state.emailHint : "";
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const rawEmail = form.elements.signinEmail.value || "";
    const normalizedEmail = rawEmail.trim().toLowerCase();
    const rawPassword = form.elements.signinPassword.value || "";
    setSubmitError("");

    if (isApiConfigured()) {
      setSubmitting(true);
      try {
        const { user, token } = await postSignin({
          email: normalizedEmail || rawEmail.trim(),
          password: rawPassword,
          signupType: signupData?.signupType,
        });
        onSignIn(user, { token });
        navigateAfterSignIn(navigate, user, { nextPath: nextParam });
        return;
      } catch (err) {
        setSubmitError(err instanceof Error ? err.message : "Sign in failed");
        setSubmitting(false);
        return;
      }
    }

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const role = signupData?.signupType || currentUser?.role || "applicant";
    const nextUser = {
      name: "CJ Jayamaha",
      email: signupData?.email || rawEmail,
      role,
      ...(role === "applicant"
        ? { profileActive: false, profileComplete: false }
        : {}),
    };

    onSignIn(nextUser);

    navigateAfterSignIn(navigate, nextUser, { nextPath: nextParam });
  };

  const formCard = (
    <section className="nd1-auth-card nd1-auth-card--signin">
      <Link className="nd1-auth-back" to="/">
        <ArrowLeft className="nd1-icon" aria-hidden="true" />
        Back to home
      </Link>
      <h2>{showWelcome ? "Welcome to JOBUWIN" : "Welcome back"}</h2>
      <p className="nd1-auth-card__lede">
        {showWelcome
          ? "Your account is verified. Sign in to continue."
          : "Good to see you again. Sign in to continue."}
      </p>

      <form className="nd1-auth-form" action="#" method="post" onSubmit={handleSubmit}>
        {passwordResetHint ? (
          <p className="nd1-auth-card__lede">
            Use your email below, then choose &ldquo;Forgot password?&rdquo; to start a reset. You can sign back
            in after updating your password.
          </p>
        ) : null}

        <label htmlFor="signinEmail">Email address</label>
        <div className="nd1-auth-field">
          <input
            id="signinEmail"
            name="signinEmail"
            type="email"
            placeholder="Enter your email"
            defaultValue={signupData?.email || resetEmailHint || ""}
            required
          />
        </div>

        <label htmlFor="signinPassword">Password</label>
        <div className="nd1-auth-field nd1-auth-field--password">
          <input
            id="signinPassword"
            name="signinPassword"
            type={showPassword ? "text" : "password"}
            placeholder="Enter your password"
            required
          />
          <button
            type="button"
            className="nd1-auth-field__toggle"
            onClick={() => setShowPassword((prev) => !prev)}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? (
              <EyeOff className="nd1-icon" aria-hidden="true" />
            ) : (
              <Eye className="nd1-icon" aria-hidden="true" />
            )}
          </button>
        </div>

        <Link className="nd1-auth-link" to="/forgot-password">
          Forgot password?
        </Link>

        {submitError ? (
          <p className="nd1-auth-alert" role="alert">
            {submitError}
          </p>
        ) : null}

        <button type="submit" className="nd1-btn nd1-btn--primary nd1-auth-submit" disabled={submitting}>
          {submitting ? "Signing in..." : "Sign in"}
          <ArrowRight className="nd1-icon" aria-hidden="true" />
        </button>
      </form>

      <p className="nd1-auth-switch">
        New to Jobuwin? <Link to={signupHref}>Create an account</Link>
      </p>
    </section>
  );

  return (
    <Nd1AuthShell hideFooter>
      <PageSeo
        title="Sign in"
        description="Sign in to JOBUWIN to continue genuine hiring conversations, manage opportunities, and connect with real people."
        path="/signin"
        canonicalPath="/signin"
        noindex
      />
      <main id="nd1-main" className="nd1-shell nd1-auth-shell nd1-auth-shell--signin">
        <div className="nd1-auth-panel">
          <Nd1SigninMarketing />
          {formCard}
        </div>
      </main>
    </Nd1AuthShell>
  );
}

export default SigninPage;
